// pages/SubscriptionManager.js - VERSION FINALE CORRIGÉE USD
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConversations } from '../context/ConversationContext';
import { loadStripe } from '@stripe/stripe-js';
import { 
  ArrowLeft, Crown, CreditCard, Check, Star, Zap, Shield, 
  RefreshCw, MessageCircle, Brain, Wand2, Calendar,
  ArrowRight, ChevronDown, ChevronUp, Sparkles
} from 'lucide-react';
import styles from './SubscriptionManager.module.css';

// ✅ DEBUG : Vérifier que la clé existe
//console.log('🔍 REACT_APP_STRIPE_PUBLISHABLE_KEY:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
//console.log('🔍 Type:', typeof process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ✅ CORRECTION : Initialiser Stripe avec vérification
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!stripeKey) {
  console.error('❌ ERREUR : REACT_APP_STRIPE_PUBLISHABLE_KEY est undefined !');
  console.error('📝 Vérifiez que :');
  console.error('   1. Le fichier frontend/.env existe');
  console.error('   2. Il contient : REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_test_...');
  console.error('   3. Vous avez redémarré le serveur (npm start)');
}

const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

const SubscriptionManager = () => {
  const { t, i18n } = useTranslation(['subscription', 'common']);
  const navigate = useNavigate();
  const { user, refreshData } = useConversations();
  const [loading, setLoading] = useState(false);
  const [purchasingPlan, setPurchasingPlan] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  
const [userCurrency, setUserCurrency] = useState('usd');
const [userCountry, setUserCountry] = useState(null);
  // ✅ DEBUG : Afficher un message au chargement
  useEffect(() => {
    if (!stripePromise) {
      alert('⚠️ Stripe non configuré ! Vérifiez la console (F12) pour plus de détails.');
    } else {
      console.log('✅ Stripe initialisé avec succès');
    }
  }, []);

  useEffect(() => { refreshData?.(); }, [refreshData]);
useEffect(() => {
    const detectCountry = async () => {
        try {
            const res = await fetch('https://ipapi.co/country/');
            const country = await res.text();
            const euroZone = ['FR','DE','IT','ES','PT','BE','NL','AT','FI','IE','LU','SK','SI','EE','LV','LT','MT','CY','GR'];
            const code = country.trim();
            setUserCountry(code);
            setUserCurrency(euroZone.includes(code) ? 'eur' : 'usd');
        } catch {
            setUserCurrency('usd');
        }
    };
    detectCountry();
}, []);

  // ✅ CONFIGURATION CORRIGÉE : Utilisation des VRAIES features
  const plans = [
    {
      id: 'gratuit',
      nameKey: 'subscription:plans.free.name',
      descriptionKey: 'subscription:plans.free.description',
      price: { monthly: 0, yearly: 0 },
      credits: 200,
      maxConversations: 3,
      popular: false,
      badge: 'free',
      badgeTextKey: 'subscription:plans.free.badge',
      badgeIcon: 'CreditCard',
      featuresKeys: [
        'subscription:plans.free.features.credits_signup',
        'subscription:plans.free.features.ice_breakers',
        'subscription:plans.free.features.message_analysis',
        'subscription:plans.free.features.conversations_max',
      ],
      featuresValues: [200, 10, 40, 3] // 200 crédits, ~10 ice-breakers, ~40 analyses, 3 convs
    },
    {
      id: 'plan1',
      nameKey: 'subscription:plans.starter.name',
      descriptionKey: 'subscription:plans.starter.description',
      price: { monthly: 4.99, yearly: 4.25 },
      credits: 1500,
      maxConversations: 10,
      popular: false,
      badge: 'starter',
      badgeTextKey: 'subscription:plans.starter.badge',
      badgeIcon: 'Star',
      featuresKeys: [
        'subscription:plans.starter.features.credits_month',
        'subscription:plans.starter.features.ice_breakers',
        'subscription:plans.starter.features.message_generation',
        'subscription:plans.starter.features.message_analysis',
        'subscription:plans.starter.features.date_planning',
        'subscription:plans.starter.features.image_analysis',
        'subscription:plans.starter.features.conversations_max',
        'subscription:plans.starter.features.support_email'
      ],
      featuresValues: [1500, 75, 100, 300, 150, 115, 10, null]
      // 1500 crédits, ~75 ice-breakers (1500/20), ~100 messages (1500/15), 
      // ~300 analyses (1500/5), ~150 dates (1500/10), ~115 screenshots (1500/13)
    },
    {
      id: 'plan2',
      nameKey: 'subscription:plans.premium.name',
      descriptionKey: 'subscription:plans.premium.description',
      price: { monthly: 9.99, yearly: 9.5 },
      credits: 5000,
      maxConversations: 30,
      popular: true,
      badge: 'popular',
      badgeTextKey: 'subscription:plans.premium.badge',
      badgeIcon: 'Sparkles',
      featuresKeys: [
        'subscription:plans.premium.features.credits_month',
        'subscription:plans.premium.features.ice_breakers',
        'subscription:plans.premium.features.message_generation',
        'subscription:plans.premium.features.message_analysis',
        'subscription:plans.premium.features.date_planning',
        'subscription:plans.premium.features.image_analysis',
        'subscription:plans.premium.features.conversations_max',
        'subscription:plans.premium.features.priority_support',
        'subscription:plans.premium.features.analytics',
        'subscription:plans.premium.features.badge_exclusive'
      ],
      featuresValues: [5000, 250, 333, 1000, 500, 384, 30, null, null, null]
      // 5000 crédits, ~250 ice-breakers (5000/20), ~333 messages (5000/15),
      // ~1000 analyses (5000/5), ~500 dates (5000/10), ~384 screenshots (5000/13)
    },
    {
      id: 'plan3',
      nameKey: 'subscription:plans.pro.name',
      descriptionKey: 'subscription:plans.pro.description',
      price: { monthly: 19.99, yearly: 18.5 },
      credits: 15000,
      maxConversations: -1,
      popular: false,
      badge: 'pro',
      badgeTextKey: 'subscription:plans.pro.badge',
      badgeIcon: 'Zap',
      featuresKeys: [
        'subscription:plans.pro.features.credits_month',
        'subscription:plans.pro.features.ice_breakers',
        'subscription:plans.pro.features.message_generation',
        'subscription:plans.pro.features.message_analysis',
        'subscription:plans.pro.features.date_planning',
        'subscription:plans.pro.features.image_analysis',
        'subscription:plans.pro.features.unlimited',
        'subscription:plans.pro.features.support_24_7',
        'subscription:plans.pro.features.analytics',
        'subscription:plans.pro.features.beta_vip',
        'subscription:plans.pro.features.api_access'
      ],
      featuresValues: [15000, 750, 1000, 3000, 1500, 1153, null, null, null, null, null]
      // 15000 crédits, ~750 ice-breakers (15000/20), ~1000 messages (15000/15),
      // ~3000 analyses (15000/5), ~1500 dates (15000/10), ~1153 screenshots (15000/13)
    }
  ];

  // FAQ traduite
  const faqKeys = ['q1', 'q2', 'q3', 'q4', 'q5'];

  const getBadgeIcon = (iconName, size = 16) => {
    const icons = {
      'CreditCard': <CreditCard size={size} />,
      'Star': <Star size={size} />,
      'Zap': <Zap size={size} />,
      'Sparkles': <Sparkles size={size} />
    };
    return icons[iconName] || <Star size={size} />;
  };

  const handleGoBack = () => {
    navigate('/dashboard');
  };

  const handleUpgrade = async (planId) => {
    if (purchasingPlan || !planId || planId === 'gratuit') return;
    
    if (!stripePromise) {
      alert('❌ Stripe non configuré. Impossible de procéder au paiement.\n\nVérifiez que REACT_APP_STRIPE_PUBLISHABLE_KEY est dans votre fichier .env');
      return;
    }
    
    setPurchasingPlan(planId);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert(t('common:errors.login_required'));
        navigate('/login');
        return;
      }

       // ✅ AJOUTER — Détecter le pays avant le checkout
      let countryCode = null;
      try {
        const geoRes = await fetch('https://ipapi.co/country/');
        countryCode = (await geoRes.text()).trim();
      } catch {
        countryCode = null; // fallback USD
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'https://rizzingbot.com/api';

      console.log('🚀 Création session checkout pour plan:', planId);

      const response = await fetch(`${apiUrl}/payment/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ planId, countryCode })
      });

      const data = await response.json();
      console.log('📊 Réponse serveur:', data);

      if (!response.ok) {
        throw new Error(data.message || t('common:errors.upgrade_failed'));
      }

      if (data.sessionUrl) {
        console.log('✅ Redirection vers Stripe Checkout:', data.sessionUrl);
        window.location.href = data.sessionUrl;
      } else if (data.sessionId) {
        console.log('✅ Utilisation de stripe.redirectToCheckout');
        const stripe = await stripePromise;
        
        if (!stripe) {
          throw new Error('Stripe non initialisé. Vérifiez REACT_APP_STRIPE_PUBLISHABLE_KEY dans .env');
        }
        
        const { error } = await stripe.redirectToCheckout({
          sessionId: data.sessionId
        });
        
        if (error) {
          throw error;
        }
      } else {
        throw new Error('Pas de sessionUrl ni sessionId dans la réponse');
      }
      
    } catch (error) {
      console.error('❌ Erreur upgrade:', error);
      alert(t('common:errors.upgrade_failed') + ': ' + error.message);
      setPurchasingPlan(null);
      setLoading(false);
    }
  };

  const getCurrentPlan = () => {
    return user?.subscription || { 
      plan_name: 'gratuit', 
      plan_display_name: 'subscription:plans.free.name'
    };
  };

  const isCurrentPlan = (planId) => {
    return getCurrentPlan().plan_name === planId;
  };

  const getDaysRemaining = () => {
    if (!user?.subscription?.expires_at) return null;
    const now = new Date();
    const expiresAt = new Date(user.subscription.expires_at);
    const diffTime = expiresAt - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getButtonText = (plan) => {
    if (isCurrentPlan(plan.id)) {
      return t('subscription:buttons.current');
    }
    if (purchasingPlan === plan.id) {
      return t('subscription:buttons.loading');
    }
    const planName = t(plan.nameKey);
    return t('subscription:buttons.choose', { plan: planName });
  };

  const currentPlan = getCurrentPlan();
  const daysRemaining = getDaysRemaining();

  return (
    <div className={styles["subscription-page"]}>
      <div className={styles["subscription-container"]}>
        
        {/* Header */}
        <div className={styles["page-header"]}>
          <button 
            onClick={handleGoBack}
            className={styles["back-button"]}
          >
            <ArrowLeft size={16} />
            {t('subscription:page.back_button')}
          </button>

          <h1 className={styles["page-title"]}>
            <Crown className={styles["title-icon"]} size={32} />
            {t('subscription:page.title')}
          </h1>
          <p className={styles["page-subtitle"]}>
            {t('subscription:page.subtitle')}
          </p>

          {user?.subscription && (
            <div className={styles["current-plan-badge"]}>
              <div className={styles["current-plan-info"]}>
              {daysRemaining !== null && (
  <div className={styles["current-plan-details"]}>
    {t('subscription:current_plan.expires', {
      count: daysRemaining,
      days: daysRemaining
    })}
  </div>
)}

              </div>
            </div>
          )}
        </div>

        {/* Billing Toggle */}
        <div className={styles["billing-toggle"]}>
          <button
            className={`${styles["toggle-button"]} ${billingCycle === 'monthly' ? styles["active"] : ''}`}
            onClick={() => setBillingCycle('monthly')}
          >
            <Calendar size={16} />
            {t('subscription:billing.monthly')}
          </button>
          {/* Billing Toggle yearly 
          <button
            className={`${styles["toggle-button"]} ${billingCycle === 'yearly' ? styles["active"] : ''}`}
            onClick={() => setBillingCycle('yearly')}
          >
            <Calendar size={16} />
            {t('subscription:billing.yearly')}
            <span className={styles["savings-badge"]}>
              {t('subscription:billing.save', { percent: 15 })}
            </span>
          </button>
          */}
        </div>

        {/* Pricing Grid */}
        <div className={styles["pricing-grid"]}>
          {plans.map((plan, index) => {
            const price = billingCycle === 'yearly' ? plan.price.yearly : plan.price.monthly;
            const isPopular = plan.popular;
            const isCurrent = isCurrentPlan(plan.id);
            
            return (
              <div 
                key={plan.id}
                className={`${styles["pricing-card"]} ${styles[plan.badge]} ${isPopular ? styles["popular"] : ''} ${isCurrent ? styles["current"] : ''}`}
              >
                {/* Badge */}
                <div className={styles[`${plan.badge}-badge`]}>
                  {getBadgeIcon(plan.badgeIcon)}
                  {t(plan.badgeTextKey)}
                </div>

                {/* Content */}
                <div className={styles["pricing-content"]}>
                  <div className={styles["plan-header"]}>
                    <h2 className={styles["plan-name"]}>{t(plan.nameKey)}</h2>
                    <p className={styles["plan-description"]}>{t(plan.descriptionKey)}</p>
                  </div>

                  {/* Prix */}
                  <div className={styles["plan-pricing"]}>
                    <div className={styles["price"]}>
                      <span className={styles["currency"]}>$</span>
                      <span className={styles["amount"]}>{price}</span>
                      <span className={styles["period"]}>{t('subscription:billing.per_month')}</span>
                    </div>
                    {billingCycle === 'yearly' && price > 0 && (
                      <div className={styles["yearly-total"]}>
                        {t('subscription:billing.billed_yearly', { 
                          amount: (price * 12).toFixed(2) 
                        })}
                      </div>
                    )}
                  </div>

                  {/* Crédits */}
                  <div className={styles["plan-credits"]}>
                    <Zap size={16} />
                    <strong>{plan.credits.toLocaleString()}</strong> {t('subscription:plans.free.credits', { count: plan.credits })}
                  </div>

                  {/* Features */}
                  <ul className={styles["features-list"]}>
                    {plan.featuresKeys.map((featureKey, idx) => (
                      <li key={idx} className={styles["feature-item"]}>
                        <Check className={styles["check-icon"]} size={16} />
                        <span>
                          {plan.featuresValues[idx] !== null 
                            ? t(featureKey, { count: plan.featuresValues[idx] })
                            : t(featureKey)
                          }
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Bouton */}
                  <button
                    className={`${styles["plan-button"]} ${isCurrent ? styles["current"] : ''} ${isPopular ? styles["popular"] : ''}`}
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={isCurrent || purchasingPlan === plan.id || plan.id === 'gratuit'}
                  >
                    {isCurrent ? <Check size={18} /> : <ArrowRight size={18} />}
                    {getButtonText(plan)}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trust Badges */}
        <div className={styles["trust-badges"]}>
          <div className={styles["trust-badge"]}>
            <Shield size={20} />
            {t('subscription:trust_badges.secure_payment')}
          </div>
          <div className={styles["trust-badge"]}>
            <RefreshCw size={20} />
            {t('subscription:trust_badges.cancel_anytime')}
          </div>
          <div className={styles["trust-badge"]}>
            <MessageCircle size={20} />
            {t('subscription:trust_badges.support_24_7')}
          </div>
        </div>

        {/* FAQ */}
        <div className={styles["faq-section"]}>
          <h2 className={styles["section-title"]}>{t('subscription:faq.title')}</h2>
          <div className={styles["faq-list"]}>
            {faqKeys.map((key, index) => (
              <div key={key} className={styles["faq-item"]}>
                <button
                  className={`${styles["faq-question"]} ${openFAQ === index ? styles["active"] : ''}`}
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                >
                  {t(`subscription:faq.${key}.question`)}
                  {openFAQ === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openFAQ === index && (
                  <div className={styles["faq-answer"]}>
                    {t(`subscription:faq.${key}.answer`)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className={styles["final-cta"]}>
          <h2>{t('subscription:final_cta.title')}</h2>
          <p>{t('subscription:final_cta.subtitle')}</p>
          <button
            className={styles["cta-button"]}
            onClick={() => !isCurrentPlan('plan2') && handleUpgrade('plan2')}
            disabled={isCurrentPlan('plan2')}
          >
            <Sparkles size={20} />
            {t('subscription:final_cta.button')}
          </button>
        </div>

      </div>
    </div>
  );
};

export default SubscriptionManager;